// Environment Configuration for Mobile Apps
// This file provides type-safe environment configuration that is determined at BUILD TIME
// DO NOT MODIFY THESE VALUES MANUALLY - They are set by build scripts

export type Environment = 'local' | 'staging' | 'production';

export interface EnvironmentConfig {
  name: Environment;
  displayName: string;
  apiUrl: string;
  wsHost: string;
  wsPort: number;
  wsScheme: 'http' | 'https';
  debug: boolean;
  // Visual indicator color for environment banner (safety feature)
  bannerColor: string;
  bannerTextColor: string;
  // Google Tag Manager container ID (optional)
  gtmId?: string | null;
}

// Environment configurations
const environments: Record<Environment, EnvironmentConfig> = {
  local: {
    name: 'local',
    displayName: 'Local Dev',
    // These are overridden at build time by vite.capacitor.config.ts
    // Update LOCAL_DEV_IP in mobile/vite.capacitor.config.ts for local testing
    apiUrl: import.meta.env.VITE_API_URL || 'http://192.168.100.51:8000',
    wsHost: import.meta.env.VITE_REVERB_HOST || '192.168.100.51',
    wsPort: Number(import.meta.env.VITE_REVERB_PORT) || 8080,
    wsScheme: (import.meta.env.VITE_REVERB_SCHEME as 'http' | 'https') || 'http',
    debug: true,
    bannerColor: '#10B981', // Green
    bannerTextColor: '#FFFFFF',
  },
  staging: {
    name: 'staging',
    displayName: 'Staging (GCP)',
    // Values injected at build time by vite.capacitor.config.ts
    apiUrl: import.meta.env.VITE_API_URL || 'https://staging.virtoly.com',
    wsHost: import.meta.env.VITE_REVERB_HOST || 'ws.virtoly.com',
    wsPort: Number(import.meta.env.VITE_REVERB_PORT) || 443,
    wsScheme: (import.meta.env.VITE_REVERB_SCHEME as 'http' | 'https') || 'https',
    debug: true,
    bannerColor: '#F59E0B', // Amber/Orange - clearly visible warning color
    bannerTextColor: '#000000',
  },
  production: {
    name: 'production',
    displayName: 'Production',
    // Values injected at build time by vite.capacitor.config.ts
    apiUrl: import.meta.env.VITE_API_URL || 'https://virtoly.com',
    wsHost: import.meta.env.VITE_REVERB_HOST || 'ws.virtoly.com',
    wsPort: Number(import.meta.env.VITE_REVERB_PORT) || 443,
    wsScheme: (import.meta.env.VITE_REVERB_SCHEME as 'http' | 'https') || 'https',
    debug: false,
    bannerColor: '#EF4444', // Red for production - to make you think twice
    bannerTextColor: '#FFFFFF',
  },
};

// This value is replaced at build time by Vite
// Default to 'production' for safety - if something goes wrong, we default to prod
const CURRENT_ENV: Environment = (import.meta.env.APP_ENV as Environment) || 'production';

// Get the current environment configuration
export const getEnvironmentConfig = (): EnvironmentConfig => {
  return environments[CURRENT_ENV];
};

// Export current environment for quick checks
export const currentEnvironment = CURRENT_ENV;

// Helper functions for environment checks
export const isDevelopment = (): boolean => CURRENT_ENV === 'local';

export const isStaging = (): boolean => CURRENT_ENV === 'staging';

export const isProductionEnv = (): boolean => CURRENT_ENV === 'production';

// Helper to check if we should show debug info
export const shouldShowDebug = (): boolean => {
  const config = getEnvironmentConfig();
  return config.debug;
};

// Helper to determine if we should show environment banner
// Shows on staging always, local always, never on production (unless forced)
export const shouldShowEnvironmentBanner = (): boolean => {
  // Never show banner on production for cleaner UX
  // But show on staging and local to remind developers/testers
  return CURRENT_ENV !== 'production';
};

